import { alpha, lighten, ThemeOptions } from "@mui/material/styles";

export const backgroundWhite = "#fbfbfb";
export const backgroundPaper = "#e6fafe";
export const textColorPrimary = "#000";

export const warningMain = "#F4F0C0";
export const warningDark = "#ddcf22";
export const warningContrastText = textColorPrimary;

export const warningButtonStyle = {
  backgroundColor: warningMain,
  color: warningContrastText,
  "&:hover": {
    backgroundColor: warningDark,
    color: warningContrastText,
  },
};

export const buttonPadding = {
  xs: "4px 8px",
  sm: "8px 16px",
  md: "16px 32px",
  lg: "16px 32px",
};

export const stColorPalette = {
  black: "#262426",
  blue: "#2b93ca",
  white: "#f2f2ed",
  red: "#662d3f",
};

// Gradients

export const gradientPaperGreenBlue =
  "linear-gradient(181deg, rgba(230, 250, 254, 1) 8%, rgba(0, 106, 119, 1) 85%, rgba(1, 50, 90, 1) 100%)";

/** Same angle/stops as gradientPaperGreenBlue, using only primary dark shades (video loading placeholder). */
export const gradientPrimaryDark =
  "linear-gradient(181deg, rgba(1, 50, 90, 1) 8%, rgba(1, 34, 66, 1) 85%, rgba(1, 34, 66, 1) 100%)";

const slColorPalette = {
  green: "#006A77",
};

export const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: "#01325A",
      dark: "#012242",
      light: "#e4e8ed",
      contrastText: "#fbfbfb",
    },
    warning: {
      main: warningMain,
      dark: warningDark,
      light: "#fbf9e5",
      contrastText: warningContrastText,
    },
    background: {
      default: backgroundWhite,
      paper: backgroundPaper,
    },
    success: {
      main: slColorPalette.green,
      light: "#bfe4d1",
      dark: "#007749",
      contrastText: "#fbfbfb",
    },
    secondary: {
      main: "#C2F2FC",
      light: "#e6fafe",
      dark: "#09b6e1",
      contrastText: textColorPrimary,
    },
    divider: "rgba(1,50,90,0.15)",
    text: {
      primary: "#000",
      secondary: "rgba(1,34,66,0.7)",
      disabled: "rgba(1,34,66,0.4)",
    },
    error: {
      main: stColorPalette.red,
      contrastText: lighten(stColorPalette.red, 0.9),
    },
    info: {
      main: stColorPalette.blue,
      contrastText: textColorPrimary,
    },
  },
  typography: {
    fontFamily: "var(--font-dm-sans), 'DM Sans', sans-serif",
    fontSize: 16,
    fontWeightMedium: 400,
    fontWeightLight: 300,
    body1: {
      fontFamily: "var(--font-dm-sans), 'DM Sans', sans-serif",
      fontSize: "clamp(1.2rem, 1.5vw, 1.3rem)",
      fontWeight: 400,
      // Add text wrapping for body text
      wordBreak: "break-word",
      hyphens: "auto",
      overflowWrap: "break-word",
    },
    body2: {
      fontFamily: "var(--font-dm-sans), 'DM Sans', sans-serif",
      fontSize: "clamp(0.8rem, 1.1vw, 1.2rem)",
      fontWeight: 400,
      // Add text wrapping for body text
      wordBreak: "break-word",
      hyphens: "auto",
      overflowWrap: "break-word",
    },
    subtitle1: {
      fontFamily: "var(--font-dm-sans), 'DM Sans', sans-serif",
      fontSize: "clamp(0.6rem, 1vw, 0.8rem)",
      fontWeight: 400,
      // Add text wrapping for body text
      wordBreak: "break-word",
      hyphens: "auto",
      overflowWrap: "break-word",
    },
    h1: {
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      fontWeight: 400,
      fontSize: "clamp(2.5rem, 5vw, 6rem)",
      // Headings use manual hyphens only
      wordBreak: "break-word",
      hyphens: "manual",
      overflowWrap: "break-word",
    },
    h2: {
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      fontWeight: 400,
      fontSize: "clamp(2.2rem, 4.2vw, 4rem)",
      // Headings use manual hyphens only
      wordBreak: "break-word",
      hyphens: "manual",
      overflowWrap: "break-word",
    },

    h3: {
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      fontSize: "clamp(2rem, 2.5vw, 3rem)",
      wordBreak: "break-word",
      hyphens: "manual",
      overflowWrap: "break-word",
    },
    h4: {
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      fontSize: "clamp(1.8rem, 2.3vw, 2.4rem)",
      wordBreak: "break-word",
      hyphens: "manual",
      overflowWrap: "break-word",
    },
    h5: {
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      fontSize: "clamp(1.6rem, 1.8vw, 1.9rem)",
      wordBreak: "break-word",
      hyphens: "manual",
      overflowWrap: "break-word",
    },
    h6: {
      fontSize: "clamp(1.4rem, 1.5vw, 1.8rem)",
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      wordBreak: "break-word",
      hyphens: "manual",
      overflowWrap: "break-word",
    },
    button: {
      fontFamily: "var(--font-host-grotesk), 'Host Grotesk', sans-serif",
      fontWeight: 400,
      fontSize: "clamp(1rem, 1.5vw, 1.5rem)",
      textTransform: "none",
      lineHeight: 1.2,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 20,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1400,
    },
  },

  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: "h1",
          h2: "h2",
          h3: "h3",
          h4: "h4",
          h5: "h5",
          h6: "h6",
          subtitle1: "p",
          subtitle2: "p",
          body1: "p",
          body2: "p",
          inherit: "p",
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        ssrMatchMedia: (query) => ({
          matches: query === "(min-width: 200px)",
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          textDecoration: "underline",
          fontWeight: 800,
          color: slColorPalette.green,
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },

    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: `${theme.palette.success.contrastText} !important`,
        }),
      },
    },

    MuiToggleButtonGroup: {
      // variants: [
      //   {
      //     props: { color: "primary" },
      //     style: ({ theme }) => ({
      //       backgroundColor: theme.palette.primary.main,
      //     }),
      //   },
      // ],
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          "& .MuiAlert-icon": {
            fontSize: theme.typography.h6.fontSize,
          },
        }),
      },
      variants: [
        {
          props: { severity: "warning" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            "& .MuiAlert-icon": {
              color: theme.palette.warning.dark,
            },
          }),
        },
        {
          props: { severity: "info" },
          style: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.info.light, 0.2),
            color: theme.palette.primary.main,
            "& .MuiAlert-icon": {
              color: theme.palette.primary.main,
            },
          }),
        },
        {
          props: { severity: "success" },
          style: ({ theme }) => ({
            color: theme.palette.success.contrastText,
            "& .MuiAlert-icon": {
              color: theme.palette.success.contrastText,
            },
          }),
        },
      ],
    },
    MuiToggleButton: {
      variants: [
        {
          props: { color: "primary" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            minWidth: "70px",
            "&:hover": {
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
            },
          }),
        },
      ],
    },

    MuiLink: {
      styleOverrides: {
        root: () => ({
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:before": {
            display: "none",
          },
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },

    MuiCard: {
      styleOverrides: {
        root: () => ({
          boxShadow: "none",
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "16px 16px",
          [theme.breakpoints.up("sm")]: {
            padding: "24px 30px",
          },
          [theme.breakpoints.up("md")]: {
            padding: "40px 40px 0px 40px",
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "16px 16px",
          [theme.breakpoints.up("sm")]: {
            padding: "24px 30px",
          },
          [theme.breakpoints.up("md")]: {
            padding: "24px 40px",
          },
        }),
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "24px 30px",
          fontSize: theme.typography.h4.fontSize,
          [theme.breakpoints.down("sm")]: {
            padding: "24px 16px",
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "24px 30px",
          fontSize: theme.typography.h4.fontSize,
          [theme.breakpoints.down("sm")]: {
            padding: "24px 16px",
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "24px 30px",
          [theme.breakpoints.down("sm")]: {
            padding: "24px 16px",
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          paddingInline: theme.spacing(2.2),
          paddingBlock: theme.spacing(2),
          boxShadow: "none",
        }),
      },
      variants: [
        {
          props: { size: "large" },
          style: ({ theme }) => ({
            "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
              fontSize: theme.typography.h3.fontSize,
              width: theme.typography.h3.fontSize,
              height: theme.typography.h3.fontSize,
            },
            fontSize: theme.typography.h3.fontSize,
          }),
        },
        {
          props: { size: "medium" },
          style: ({ theme }) => ({
            "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
              fontSize: theme.typography.h6.fontSize,
              width: theme.typography.h6.fontSize,
              height: theme.typography.h6.fontSize,
            },
            fontSize: theme.typography.h6.fontSize,
          }),
        },
        {
          props: { size: "small" },
          style: ({ theme }) => ({
            "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
              fontSize: theme.typography.body1.fontSize,
              width: theme.typography.body1.fontSize,
              height: theme.typography.body1.fontSize,
            },
            fontSize: theme.typography.body1.fontSize,
            p: "3px 12px !important",
          }),
        },
        {
          props: { variant: "text", color: "primary" },
          style: ({ theme }) => ({
            color: theme.palette.success.main,
            "&:hover": {
              color: theme.palette.success.main,
            },
          }),
        },
        {
          props: { variant: "text", color: "secondary" },
          style: ({ theme }) => ({
            color: theme.palette.warning.dark,
            "&:hover": {
              color: theme.palette.warning.dark,
            },
          }),
        },
        {
          props: { variant: "contained", color: "primary" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.light,
            },
          }),
        },
        {
          props: { variant: "contained", color: "secondary" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.primary.dark,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.primary.light,
            },
          }),
        },
        {
          props: { variant: "contained", color: "warning" },
          style: () => ({
            ...warningButtonStyle,
          }),
        },
        {
          props: { variant: "contained", color: "success" },
          style: ({ theme }) => ({
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              color: theme.palette.success.contrastText,
            },
          }),
        },
      ],
    },

    MuiDialog: {
      defaultProps: {
        transitionDuration: 0.2,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ".MuiDialogContent-root": {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.secondary.dark,
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.primary.dark,
              borderRadius: "6px",
            },
            scrollBarWidth: "thin",
            scrollBarColor: theme.palette.primary.dark,
          },
          ".MuiDialog-paper": {
            // Reduce the spacing around the dialog content on small devices
            [theme.breakpoints.down("sm")]: {
              margin: "12px",
              width: "calc(100% - 24px)",
              maxHeight: "calc(100% - 24px)",
            },
            [theme.breakpoints.up("xl")]: {
              width: "1200px",
            },
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          "&:hover": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
          p: 2,
        }),
      },
    },
  },
};
